import { useEffect, useState } from "react";

import { useApi } from "@backstage/core-plugin-api";
import { githubApiRef } from "@internal/backstage-plugin-github-react";
import { getDataOrThrow } from "@internal/backstage-plugin-utility-common";

const PG_REFERENCE_CHART_FILE = "jh-pg-reference-chart.json";

/**
 * A single entry of `jh-pg-reference-chart.json` in `Banno/gcp-org-hierarchy`,
 * the source of truth mapping a product group code to its business unit.
 */
interface PgReferenceEntry {
    /** Product group code, e.g. `"pmt-remit"`. */
    pg: string;
    /** Business unit display name, e.g. `"Payments"`. */
    bu: string;
    /** Vice president responsible for the business unit. */
    vp?: string;
}

/**
 * Fetches the list of GCP product group codes from GitHub.
 *
 * @returns An object containing the `businessUnits` list and a `loading` flag.
 */
export function useGcpBusinessUnits(): {
    businessUnits: string[];
    loading: boolean;
} {
    const client = useApi(githubApiRef);
    const [businessUnits, setBusinessUnits] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const run = async () => {
            try {
                // TODO: Use canonical org casing ("Banno") or centralize the owner value to reduce credential resolution risk.
                const referenceChart = getDataOrThrow(
                    await client.repo.getFileContent(
                        "banno",
                        "gcp-org-hierarchy",
                        PG_REFERENCE_CHART_FILE
                    )
                );
                const entries: PgReferenceEntry[] = JSON.parse(referenceChart);
                if (!Array.isArray(entries)) {
                    return;
                }
                setBusinessUnits(
                    entries
                        .map(entry => entry?.pg)
                        .filter((pg): pg is string => Boolean(pg))
                );
            } catch (e) {
                // TODO: Return an explicit error state so the field can render an empty-state message.
                // eslint-disable-next-line no-console
                console.error(
                    "useGcpBusinessUnits: failed to fetch business units",
                    e
                );
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [client]);

    return { businessUnits, loading };
}
